import { MarkObj } from 'rc-slider/lib/Marks';
import React, { FC, useEffect, useState, ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import {
    DEPTH,
    MAX_INPUT,
    POSITION_CLOSE_TYPE,
    POSITION_SIDE,
    PRICE_INCREMENT_DECREMENT_VALUE,
} from 'src/common/constants';
import { currencyFormatter, roundNumber } from 'src/common/helpers';
import {
    Button,
    Modal,
    ModalTitle,
    ModalContent,
    ModalActions,
    Input,
    Range,
    Select,
    SelectItem,
} from 'src/components/core';
import { Cross } from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import {
    setPositionStopProfitLoss,
    cancelPositionStopProfitLoss,
} from 'src/setup/redux/slices/derivativesSlice';
import { Position, SetStopProfitLossPayLoad, StopProfitLoss, numberKeyPairValues } from 'src/types';

interface PositionTPSLModalProps {
    open: boolean;
    position: Position;
    positionStopProfitLoss: StopProfitLoss;
    handleClose: () => void;
}

interface RangeMarks {
    [key: number]: Record<string | number, ReactNode | MarkObj>;
}
const StyledModal = styled(Modal)`
    ${({ theme: { colors, typography, breakpoints } }) => `
        .modal-paper {
            border-radius: 8px;
            @media (min-width: ${breakpoints.md}) {
                border-radius: 10px;
                width: 447px;
            }
        }
        .modal-title {
            padding: 20px 24px 0;
            @media (max-width: ${breakpoints.md}) {
                padding: 20px 16px 0;
            }
            > div {
                font-size: ${typography.size.desktop.paragraph.p1};
                line-height: ${typography.lineHeight[4]};
                font-weight: ${typography.weight.medium};
                @media (max-width: ${breakpoints.md}) {
                    font-size: ${typography.size.desktop.paragraph.p2};
                }
            }
        }
        .modal-content {
            padding: 32px 24px 24px;
            @media (max-width: ${breakpoints.md}) {
                padding: 32px 16px 24px;
            }
            .input-container {
                margin-bottom: 0px;
            }
            .value-up-down {
                span {
                    &:nth-of-type(1) {
                        padding-right: 10px;
                    }
                    &:nth-of-type(3) {
                        padding-left: 10px;
                    }
                }
            }
        }
        .modal-actions {
            padding: 20px 24px 30px;
            gap: 16px;
            justify-content: space-between;
            button {
                line-height: ${typography.lineHeight[4]};
                font-weight: ${typography.weight.medium};
                padding: 10px 68px;
                @media (max-width: ${breakpoints.md}) {
                        padding: 8px 46px;
                }
                @media (max-width: ${breakpoints.sm}) {
                    padding: 8px 32px;
                }
                &:nth-of-type(2) {
                    color: ${colors.text.tertiary};
                }
            }
        }
    `}
`;

const LabelText = styled.div`
    ${({ theme: { colors, typography } }) => `
        font-size: ${typography.size.desktop.paragraph.p3}; 
        color: ${colors.text.secondary};
        line-height: calc(${typography.lineHeight[2]} - 1px);
        font-weight: ${typography.weight.normal};
    `}
`;

const LargeText = styled.div<{ color: string }>`
    ${({ theme: { typography, breakpoints }, color }) => `
        font-size: ${typography.size.desktop.paragraph.p2}; 
        color: ${color};
        line-height: ${typography.lineHeight[3]};
        font-weight: ${typography.weight.normal};
        @media (max-width: ${breakpoints.md}) {
            font-size: ${typography.size.desktop.paragraph.p3}; 
            line-height: calc(${typography.lineHeight[2]} - 1px);
        }
    `}
`;

const TitleText = styled.div`
    display: inline-block;
`;

const Divider = styled.div`
    ${({ theme: { colors, breakpoints } }) => `
        width: 100%;
        border: 1px solid ${colors.border.light};
        margin: 32px 0;
        @media (max-width: ${breakpoints.sm}) {
            margin: 24px 0;
        }
        `}
`;

const StyledSpan = styled.span`
    ${({ color }) => `
        color: ${color};
        cursor: pointer;
    `}
`;

const InfoMessage = styled(FlexContainer)`
    ${({ theme: { colors, typography } }) => `
        margin-top: 16px;
        padding: 10px;
        background: ${colors.background.secondary.v2};
        border-radius: 4px;
        color: ${colors.text.tertiary};
        font-size: ${typography.size.mobile.paragraph.p3};
        font-weight: ${typography.weight.normal};
        line-height: ${typography.lineHeight[1]};
        span {
            color: ${colors.text.primary};
        }
    `}
`;

const PositionTPSLModal: FC<PositionTPSLModalProps> = ({
    open,
    position,
    positionStopProfitLoss,
    handleClose,
}) => {
    const { t } = useTranslation();
    const { colors } = useTheme();
    const inputName = {
        TAKE_PROFIT: 'takeProfit',
        TAKE_PROFIT_PERCENTAGE: 'takeProfitPercentage',
        STOP_LOSS: 'stopLoss',
        STOP_LOSS_PERCENTAGE: 'stopLossPercentage',
    };
    const inputType = { USDT: 'USDT', ROI: '%' };
    const matchingPriceType: numberKeyPairValues = { LAST_TRADED_PRICE: 0, INDEX_PRICE: 1 };
    const maxTakeProfitRange: numberKeyPairValues = { USDT: 25, '%': 150 };
    const maxStopLossRange: numberKeyPairValues = { USDT: 25, '%': 75 };
    const rangeMarks: RangeMarks = {
        25: { 0: 0, 5: 5, 10: 10, 15: 15, 20: 20, 25: 25 },
        75: { 0: 0, 25: 25, 50: 50, 75: 75 },
        100: { 0: 0, 25: 25, 50: 50, 75: 75, 100: 100 },
        150: { 0: 0, 25: 25, 50: 50, 75: 75, 100: 100, 125: 125, 150: 150 },
    };
    const [takeProfit, setTakeProfit] = useState('0');
    const [takeProfitPercentage, setTakeProfitPercentage] = useState('0');
    const [takeProfitPriceInputType, setTakeProfitPriceInputType] = useState(inputType.USDT);
    const [takeProfitPriceType, setTakeProfitPriceType] = useState(
        matchingPriceType.LAST_TRADED_PRICE,
    );
    const [stopLoss, setStopLoss] = useState('0');
    const [stopLossPercentage, setStopLossPercentage] = useState('0');
    const [stopLossPriceInputType, setStopLossPriceInputType] = useState(inputType.USDT);
    const [stopLossPriceType, setStopLossPriceType] = useState(matchingPriceType.LAST_TRADED_PRICE);
    const [expectedTakeProfitPrice, setExpectedTakeProfitPrice] = useState(0);
    const [expectedProfit, setExpectedProfit] = useState(0);
    const [expectedProfitROI, setExpectedProfitROI] = useState(0);
    const [expectedStopLossPrice, setExpectedStopLossPrice] = useState(0);
    const [expectedLoss, setExpectedLoss] = useState(0);
    const [expectedLossROI, setExpectedLossROI] = useState(0);
    const isPositionTakeProfitExist = useRef(false);
    const isPositionStopLossExist = useRef(false);
    const futuresSymbol = useAppSelector((state) => state.global.configV2.futuresSymbol);
    const currentSymbol = futuresSymbol[position.symbolId];
    const setPositionStopProfitLossLoading = useAppSelector(
        (state) => state.derivatives.loading.setPositionStopProfitLoss,
    );
    const cancelPositionStopProfitLossLoading = useAppSelector(
        (state) => state.derivatives.loading.cancelPositionStopProfitLoss,
    );
    const { userCurrentFutureTradeAccountIndex } = useAppSelector((state) => state.global);
    const entryPrice = Number(position.avgPrice);
    const maxPriceDecimals = DEPTH[currentSymbol?.minPricePrecision];
    // regular expression to validate price
    const priceRegEx = new RegExp(
        `^[0-9]*${
            maxPriceDecimals > 0
                ? // eslint-disable-next-line prefer-template
                  '(\\.[0-9]{0,' + maxPriceDecimals + '})?$'
                : '?$'
        }`,
    );

    const dispatch = useAppDispatch();

    const handlePriceChange = (
        value: string | number | number[] | boolean,
        name: string | undefined,
    ) => {
        if (Number(value) > MAX_INPUT) {
            return;
        }
        value = value === '.' ? '0.' : value;

        if (name === inputName.TAKE_PROFIT) {
            if (Number(value) === 0) {
                setTakeProfit(value.toString());
                setTakeProfitPercentage(value.toString());
                return;
            }
            if (takeProfitPriceInputType === inputType.USDT && priceRegEx.test(value.toString())) {
                setTakeProfit(value.toString());
                let percentage = 0;
                let priceDifference = 0;
                if (position.isLong === POSITION_SIDE.LONG && Number(value) >= entryPrice) {
                    priceDifference = Number(value) - entryPrice;
                } else if (position.isLong === POSITION_SIDE.SHORT && Number(value) <= entryPrice) {
                    priceDifference = entryPrice - Number(value);
                }
                percentage = (priceDifference * 100) / entryPrice;
                percentage =
                    percentage > maxTakeProfitRange[takeProfitPriceInputType]
                        ? maxTakeProfitRange[takeProfitPriceInputType]
                        : percentage;
                setTakeProfitPercentage(percentage.toFixed(2));
            } else if (
                takeProfitPriceInputType === inputType.ROI &&
                Number(value) >= 0 &&
                Number(value) <= maxTakeProfitRange[takeProfitPriceInputType]
            ) {
                setTakeProfit(value.toString());
                setTakeProfitPercentage(value.toString());
            }
        } else if (name === inputName.TAKE_PROFIT_PERCENTAGE) {
            if (Number(value) === 0) {
                setTakeProfit(value.toString());
                setTakeProfitPercentage(value.toString());
                return;
            }
            if (takeProfitPriceInputType === inputType.USDT && priceRegEx.test(value.toString())) {
                setTakeProfitPercentage(Number(value).toFixed(2));

                let priceDifference = (Number(value) * entryPrice) / 100;
                let takeProfitPrice = entryPrice;
                if (position.isLong === POSITION_SIDE.LONG) {
                    takeProfitPrice = takeProfitPrice + priceDifference;
                } else if (position.isLong === POSITION_SIDE.SHORT) {
                    takeProfitPrice = takeProfitPrice - priceDifference;
                }
                setTakeProfit(roundNumber(takeProfitPrice, maxPriceDecimals).toString());
            } else if (takeProfitPriceInputType === inputType.ROI && Number(value) >= 0) {
                value =
                    Number(value) > maxTakeProfitRange[takeProfitPriceInputType]
                        ? maxTakeProfitRange[takeProfitPriceInputType]
                        : value;
                setTakeProfit(value.toString());
                setTakeProfitPercentage(value.toString());
            }
        } else if (name === inputName.STOP_LOSS) {
            if (Number(value) === 0) {
                setStopLoss(value.toString());
                setStopLossPercentage(value.toString());
                return;
            }
            if (stopLossPriceInputType === inputType.USDT && priceRegEx.test(value.toString())) {
                setStopLoss(value.toString());
                let percentage = 0;
                let priceDifference = 0;
                if (position.isLong === POSITION_SIDE.SHORT && Number(value) >= entryPrice) {
                    priceDifference = Number(value) - entryPrice;
                } else if (position.isLong === POSITION_SIDE.LONG && Number(value) <= entryPrice) {
                    priceDifference = entryPrice - Number(value);
                }
                percentage = (priceDifference * 100) / entryPrice;
                percentage =
                    percentage > maxStopLossRange[stopLossPriceInputType]
                        ? maxStopLossRange[stopLossPriceInputType]
                        : percentage;
                setStopLossPercentage(percentage.toFixed(2));
            } else if (
                stopLossPriceInputType === inputType.ROI &&
                Number(value) >= 0 &&
                Number(value) <= maxStopLossRange[stopLossPriceInputType]
            ) {
                setStopLoss(value.toString());
                setStopLossPercentage(value.toString());
            }
        } else if (name === inputName.STOP_LOSS_PERCENTAGE) {
            if (Number(value) === 0) {
                setStopLoss(value.toString());
                setStopLossPercentage(value.toString());
                return;
            }
            if (stopLossPriceInputType === inputType.USDT && priceRegEx.test(value.toString())) {
                setStopLossPercentage(Number(value).toFixed(2));

                let priceDifference = (Number(value) * entryPrice) / 100;
                let stopLossPrice = entryPrice;
                if (position.isLong === POSITION_SIDE.LONG) {
                    stopLossPrice = stopLossPrice - priceDifference;
                } else if (position.isLong === POSITION_SIDE.SHORT) {
                    stopLossPrice = stopLossPrice + priceDifference;
                }
                setStopLoss(roundNumber(stopLossPrice, maxPriceDecimals).toString());
            } else if (stopLossPriceInputType === inputType.ROI && Number(value) >= 0) {
                value =
                    Number(value) > maxStopLossRange[stopLossPriceInputType]
                        ? maxStopLossRange[stopLossPriceInputType]
                        : value;
                setStopLoss(value.toString());
                setStopLossPercentage(value.toString());
            }
        }
    };

    useEffect(() => {
        if (positionStopProfitLoss) {
            if (positionStopProfitLoss.stopProfit) {
                isPositionTakeProfitExist.current = true;
                setTakeProfit(positionStopProfitLoss.stopProfitPrice);
                const takeProfitPriceDifference =
                    position.isLong === POSITION_SIDE.LONG
                        ? Number(positionStopProfitLoss.stopProfitPrice) - entryPrice
                        : entryPrice - Number(positionStopProfitLoss.stopProfitPrice);
                const profitPercentage = (takeProfitPriceDifference * 100) / entryPrice;
                if (profitPercentage < 0) {
                    setTakeProfitPercentage('0');
                } else if (profitPercentage > maxTakeProfitRange[takeProfitPriceInputType]) {
                    setTakeProfitPercentage(
                        maxTakeProfitRange[takeProfitPriceInputType].toString(),
                    );
                } else {
                    setTakeProfitPercentage(profitPercentage.toFixed(2));
                }
            }

            if (positionStopProfitLoss.stopLoss) {
                isPositionStopLossExist.current = true;
                setStopLoss(positionStopProfitLoss.stopLossPrice);
                let stopLossPriceDifference =
                    position.isLong === POSITION_SIDE.LONG
                        ? entryPrice - Number(positionStopProfitLoss.stopLossPrice)
                        : Number(positionStopProfitLoss.stopLossPrice) - entryPrice;
                let stopLossPercentage = (stopLossPriceDifference * 100) / entryPrice;
                if (stopLossPercentage < 0) {
                    setStopLossPercentage('0');
                } else if (stopLossPercentage > maxStopLossRange[stopLossPriceInputType]) {
                    setStopLossPercentage(maxStopLossRange[stopLossPriceInputType].toString());
                } else {
                    setStopLossPercentage(stopLossPercentage.toFixed(2));
                }
            }

            setTakeProfitPriceType(positionStopProfitLoss.stopProfitTriggerConditionType);
            setStopLossPriceType(positionStopProfitLoss.stopLossTriggerConditionType);
        }
    }, [positionStopProfitLoss]);

    const updateExpectedValuesByUSDT = (expectedPrice: number, isTakeProfit: boolean) => {
        const positionValue = entryPrice * position.total;
        const totalClosingValue = position.total * expectedPrice;

        let expectedValue;
        if (isTakeProfit) {
            expectedValue =
                position.isLong === POSITION_SIDE.LONG
                    ? totalClosingValue - positionValue
                    : positionValue - totalClosingValue;
        } else {
            expectedValue =
                position.isLong === POSITION_SIDE.LONG
                    ? positionValue - totalClosingValue
                    : totalClosingValue - positionValue;
        }

        const returnOnInvestment = (expectedValue * 100) / position.margin;

        if (isTakeProfit) {
            setExpectedTakeProfitPrice(Number(expectedPrice.toFixed(maxPriceDecimals)));
            setExpectedProfitROI(returnOnInvestment);
            setExpectedProfit(expectedValue);
        } else {
            setExpectedStopLossPrice(Number(expectedPrice.toFixed(maxPriceDecimals)));
            setExpectedLossROI(returnOnInvestment);
            setExpectedLoss(expectedValue);
        }
    };

    const updateExpectedValuesByROI = (expectedROI: number, isTakeProfit: boolean) => {
        const positionValue = entryPrice * position.total;
        const expectedValue = (Number(position.margin) * Number(expectedROI)) / 100;

        let expectedPositionValue;
        if (isTakeProfit) {
            expectedPositionValue =
                position.isLong === POSITION_SIDE.LONG
                    ? positionValue + expectedValue
                    : positionValue - expectedValue;
        } else {
            expectedPositionValue =
                position.isLong === POSITION_SIDE.LONG
                    ? positionValue - expectedValue
                    : positionValue + expectedValue;
        }
        const expectedPrice = expectedPositionValue / position.total;

        if (isTakeProfit) {
            setExpectedTakeProfitPrice(Number(expectedPrice.toFixed(maxPriceDecimals)));
            setExpectedProfitROI(expectedROI);
            setExpectedProfit(expectedValue);
        } else {
            setExpectedStopLossPrice(Number(expectedPrice.toFixed(maxPriceDecimals)));
            setExpectedLossROI(expectedROI);
            setExpectedLoss(expectedValue);
        }
    };

    useEffect(() => {
        if (takeProfitPriceInputType === inputType.USDT) {
            updateExpectedValuesByUSDT(Number(takeProfit), true);
        } else if (takeProfitPriceInputType === inputType.ROI) {
            updateExpectedValuesByROI(Number(takeProfit), true);
        }
    }, [takeProfit]);

    useEffect(() => {
        if (stopLossPriceInputType === inputType.USDT) {
            updateExpectedValuesByUSDT(Number(stopLoss), false);
        } else if (stopLossPriceInputType === inputType.ROI) {
            updateExpectedValuesByROI(Number(stopLoss), false);
        }
    }, [stopLoss]);

    const handleConfirm = () => {
        const setStopProfitLossPayLoad: SetStopProfitLossPayLoad = {
            symbol_id: position.symbolId,
            is_long: Number(position.isLong),
            exchange_id: Number(position.exchangeId),
            account_index: userCurrentFutureTradeAccountIndex,
        };

        if (Number(takeProfit) > 0 || Number(stopLoss) > 0) {
            if (Number(takeProfit) > 0) {
                if (takeProfitPriceInputType === inputType.ROI) {
                    let positionMargin = Number(position.margin);
                    let expectedTakeProfit = (positionMargin * Number(takeProfit)) / 100;
                    let expectedMargin = position.avgPrice * position.total;
                    expectedMargin =
                        position.isLong === POSITION_SIDE.LONG
                            ? expectedMargin + expectedTakeProfit
                            : expectedMargin - expectedTakeProfit;
                    setStopProfitLossPayLoad.stop_profit_price = expectedMargin / position.total;
                } else {
                    setStopProfitLossPayLoad.stop_profit_price = Number(takeProfit);
                }
                setStopProfitLossPayLoad.sp_close_type = POSITION_CLOSE_TYPE.ALL_POSITION;
                setStopProfitLossPayLoad.sp_trigger_condition_type = takeProfitPriceType;
            }

            if (Number(stopLoss) > 0) {
                if (stopLossPriceInputType === inputType.ROI) {
                    let positionMargin = Number(position.margin);
                    let expectedStopLoss = (positionMargin * Number(stopLoss)) / 100;
                    let expectedMargin = position.avgPrice * position.total;
                    expectedMargin =
                        position.isLong === POSITION_SIDE.LONG
                            ? expectedMargin - expectedStopLoss
                            : expectedMargin + expectedStopLoss;
                    setStopProfitLossPayLoad.stop_loss_price = expectedMargin / position.total;
                } else {
                    setStopProfitLossPayLoad.stop_loss_price = Number(stopLoss);
                }

                setStopProfitLossPayLoad.sl_close_type = POSITION_CLOSE_TYPE.ALL_POSITION;
                setStopProfitLossPayLoad.sl_trigger_condition_type = stopLossPriceType;
            }
            dispatch(setPositionStopProfitLoss(setStopProfitLossPayLoad));
        } else {
            dispatch(cancelPositionStopProfitLoss(setStopProfitLossPayLoad));
        }
        closeHandler();
    };
    const closeHandler = () => {
        resetExpectedValues();
        handleClose();
    };
    const resetExpectedValues = () => {
        setExpectedTakeProfitPrice(0);
        setExpectedProfitROI(0);
        setExpectedProfit(0);
        setExpectedStopLossPrice(0);
        setExpectedLossROI(0);
        setExpectedLoss(0);
    };
    useEffect(() => {
        if (!isPositionTakeProfitExist.current) {
            handlePriceChange(
                0,
                takeProfitPriceInputType === inputType.USDT
                    ? inputName.TAKE_PROFIT
                    : inputName.TAKE_PROFIT_PERCENTAGE,
            );
        }
        isPositionTakeProfitExist.current = false;
    }, [takeProfitPriceInputType]);

    useEffect(() => {
        if (!isPositionStopLossExist.current) {
            handlePriceChange(
                0,
                stopLossPriceInputType === inputType.USDT
                    ? inputName.STOP_LOSS
                    : inputName.STOP_LOSS_PERCENTAGE,
            );
        }
        isPositionStopLossExist.current = false;
    }, [stopLossPriceInputType]);

    const handlePriceIncrementDecrement = (isTakeProfit: boolean, isIncrement: boolean) => {
        const inputTypeToUse = isTakeProfit ? takeProfitPriceInputType : stopLossPriceInputType;
        const currentValue = isTakeProfit ? takeProfit : stopLoss;

        const avgPriceFallback =
            inputTypeToUse === inputType.USDT && (currentValue === '0' || currentValue === '')
                ? Number(position.avgPrice)
                : Number(currentValue);

        const newValue = isIncrement
            ? avgPriceFallback + PRICE_INCREMENT_DECREMENT_VALUE
            : avgPriceFallback - PRICE_INCREMENT_DECREMENT_VALUE;

        if (newValue > 0) {
            if (isTakeProfit) {
                handlePriceChange(newValue.toString(), inputName.TAKE_PROFIT);
            } else {
                handlePriceChange(newValue.toString(), inputName.STOP_LOSS);
            }
        }
    };
    return (
        <StyledModal size="md" open={open}>
            <ModalTitle>
                <TitleText>{t('tp_sl')}</TitleText>
                <Cross
                    fill={colors.text.secondary}
                    onClick={closeHandler}
                    className="clickable"
                    style={{ height: 20, width: 20 }}
                />
            </ModalTitle>
            <ModalContent>
                <FlexContainer
                    styles={{ flexDirection: 'column', width: '100%', alignItems: 'start' }}
                >
                    <FlexContainer
                        styles={{
                            justifyContent: 'space-between',
                            width: '100%',
                            marginBottom: '12px',
                        }}
                    >
                        <LargeText color={colors.text.secondary}>{t('entry_price')}</LargeText>
                        <LargeText color={colors.text.primary}>
                            {currencyFormatter(
                                position.avgPrice,
                                DEPTH[currentSymbol?.minPricePrecision],
                            )}
                        </LargeText>
                    </FlexContainer>
                    <FlexContainer
                        styles={{
                            justifyContent: 'space-between',
                            width: '100%',
                            marginBottom: '32px',
                        }}
                    >
                        <LargeText color={colors.text.secondary}>{t('liq_price')}</LargeText>
                        <LargeText color={colors.alert.yellow}>
                            {currencyFormatter(
                                position.liquidationPrice,
                                DEPTH[currentSymbol?.minPricePrecision],
                            )}
                        </LargeText>
                    </FlexContainer>
                    <FlexContainer
                        styles={{
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            marginBottom: '8px',
                        }}
                    >
                        <LabelText>{`${t('take_profit')} ${currentSymbol.quoteTokenId}`}</LabelText>
                        <Select
                            value={takeProfitPriceType}
                            onChange={setTakeProfitPriceType}
                            headStyles={{
                                justifyContent: 'end',
                                border: 'none',
                                padding: 0,
                                width: '125px',
                                color: colors.text.secondary,
                            }}
                        >
                            {Object.keys(matchingPriceType).map((key: string) => (
                                <SelectItem key={key} value={matchingPriceType[key]}>
                                    {t(key.toLowerCase())}
                                </SelectItem>
                            ))}
                        </Select>
                    </FlexContainer>
                    <FlexContainer
                        styles={{
                            justifyContent: 'space-between',
                            width: '100%',
                            alignItems: 'center',
                            gap: '12px',
                            marginBottom: '8px',
                        }}
                    >
                        <Input
                            name={inputName.TAKE_PROFIT}
                            hasTooltip={false}
                            value={takeProfit}
                            fullWidth
                            onChange={handlePriceChange}
                            rightElement={
                                <FlexContainer
                                    className="value-up-down"
                                    styles={{
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '39px',
                                    }}
                                >
                                    <StyledSpan
                                        onClick={() => {
                                            handlePriceIncrementDecrement(true, false);
                                        }}
                                    >
                                        -
                                    </StyledSpan>
                                    <StyledSpan color={colors.text.tertiary}>|</StyledSpan>
                                    <StyledSpan
                                        onClick={() => {
                                            handlePriceIncrementDecrement(true, true);
                                        }}
                                    >
                                        +
                                    </StyledSpan>
                                </FlexContainer>
                            }
                        />
                        <Select
                            value={takeProfitPriceInputType}
                            onChange={setTakeProfitPriceInputType}
                            name={'takeProfitPriceInputType'}
                            headStyles={{ justifyContent: 'end !important', width: '118px' }}
                        >
                            {Object.values(inputType).map((value: string, index) => (
                                <SelectItem key={index} value={value}>
                                    {value}
                                </SelectItem>
                            ))}
                        </Select>
                    </FlexContainer>
                    <Range
                        name={inputName.TAKE_PROFIT_PERCENTAGE}
                        value={Number(takeProfitPercentage)}
                        onChange={handlePriceChange}
                        max={maxTakeProfitRange[takeProfitPriceInputType]}
                        marks={rangeMarks[maxTakeProfitRange[takeProfitPriceInputType]]}
                    />
                    {Number(expectedTakeProfitPrice) > 0 &&
                    ((position.isLong === POSITION_SIDE.LONG &&
                        Number(expectedTakeProfitPrice) < Number(position.liquidationPrice)) ||
                        (position.isLong === POSITION_SIDE.SHORT &&
                            Number(expectedTakeProfitPrice) >
                                Number(position.liquidationPrice))) ? (
                        <InfoMessage>
                            <p>
                                {t('take_profit_liquidation_message', {
                                    compare: t(
                                        position.isLong === POSITION_SIDE.LONG ? 'lower' : 'upper',
                                    ),
                                    liquidationPrice: `${currencyFormatter(
                                        position.liquidationPrice,
                                        DEPTH[currentSymbol?.minPricePrecision],
                                    )} ${currentSymbol.quoteTokenId}`,
                                })}
                            </p>
                        </InfoMessage>
                    ) : (
                        Number(expectedTakeProfitPrice) > 0 &&
                        Number(takeProfit) > 0 && (
                            <InfoMessage>
                                <p>
                                    {`${t('take_profit_message', {
                                        priceType: t(
                                            takeProfitPriceType ===
                                                matchingPriceType.LAST_TRADED_PRICE
                                                ? 'last_traded_price'
                                                : 'index_price',
                                        ),
                                        takeProfit: currencyFormatter(
                                            Number(expectedTakeProfitPrice),
                                            DEPTH[currentSymbol?.minPricePrecision],
                                        ),
                                    })} ${t(
                                        expectedProfit > 0
                                            ? 'expected_profit_message'
                                            : 'expected_loss_message',
                                        {
                                            expectedValue: `${currencyFormatter(
                                                Math.abs(expectedProfit),
                                                DEPTH[currentSymbol?.minPricePrecision],
                                            )} ${currentSymbol.quoteTokenId}`,
                                            ROI: currencyFormatter(expectedProfitROI, 2, 2),
                                        },
                                    )}`}
                                </p>
                            </InfoMessage>
                        )
                    )}

                    <Divider />
                    <FlexContainer
                        styles={{
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            marginBottom: '8px',
                        }}
                    >
                        <LabelText>{`${t('stop_loss')} ${currentSymbol.quoteTokenId}`}</LabelText>
                        <Select
                            value={stopLossPriceType}
                            onChange={setStopLossPriceType}
                            headStyles={{
                                justifyContent: 'end',
                                border: 'none',
                                padding: 0,
                                width: '125px',
                                color: colors.text.secondary,
                            }}
                        >
                            {Object.keys(matchingPriceType).map((key: string) => (
                                <SelectItem key={key} value={matchingPriceType[key]}>
                                    {t(key.toLowerCase())}
                                </SelectItem>
                            ))}
                        </Select>
                    </FlexContainer>
                    <FlexContainer
                        styles={{
                            justifyContent: 'space-between',
                            width: '100%',
                            alignItems: 'center',
                            gap: '12px',
                            marginBottom: '12px',
                        }}
                    >
                        <Input
                            name={inputName.STOP_LOSS}
                            hasTooltip={false}
                            value={stopLoss}
                            fullWidth
                            onChange={handlePriceChange}
                            rightElement={
                                <FlexContainer
                                    className="value-up-down"
                                    styles={{
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '39px',
                                    }}
                                >
                                    <StyledSpan
                                        onClick={() => {
                                            handlePriceIncrementDecrement(false, false);
                                        }}
                                    >
                                        -
                                    </StyledSpan>
                                    <StyledSpan color={colors.text.tertiary}>|</StyledSpan>
                                    <StyledSpan
                                        onClick={() => {
                                            handlePriceIncrementDecrement(false, true);
                                        }}
                                    >
                                        +
                                    </StyledSpan>
                                </FlexContainer>
                            }
                        />
                        <Select
                            value={stopLossPriceInputType}
                            onChange={setStopLossPriceInputType}
                            name={'stopLossPriceInputType'}
                            headStyles={{
                                justifyContent: 'end !important',
                                width: '118px',
                            }}
                        >
                            {Object.values(inputType).map((value: string, index) => (
                                <SelectItem key={index} value={value}>
                                    {value}
                                </SelectItem>
                            ))}
                        </Select>
                    </FlexContainer>
                    <Range
                        name={inputName.STOP_LOSS_PERCENTAGE}
                        value={Number(stopLossPercentage)}
                        onChange={handlePriceChange}
                        max={maxStopLossRange[stopLossPriceInputType]}
                        marks={rangeMarks[maxStopLossRange[stopLossPriceInputType]]}
                    />
                    {Number(expectedStopLossPrice) > 0 &&
                    ((position.isLong === POSITION_SIDE.LONG &&
                        Number(expectedStopLossPrice) < Number(position.liquidationPrice)) ||
                        (position.isLong === POSITION_SIDE.SHORT &&
                            Number(expectedStopLossPrice) > Number(position.liquidationPrice))) ? (
                        <InfoMessage>
                            <p>
                                {t('stop_loss_liquidation_message', {
                                    compare: t(
                                        position.isLong === POSITION_SIDE.LONG ? 'lower' : 'upper',
                                    ),
                                    liquidationPrice: `${currencyFormatter(
                                        position.liquidationPrice,
                                        DEPTH[currentSymbol?.minPricePrecision],
                                    )} ${currentSymbol.quoteTokenId}`,
                                })}
                            </p>
                        </InfoMessage>
                    ) : (
                        Number(expectedStopLossPrice) > 0 &&
                        Number(stopLoss) > 0 && (
                            <InfoMessage>
                                <p>
                                    {`${t('stop_loss_message', {
                                        priceType: t(
                                            stopLossPriceType ===
                                                matchingPriceType.LAST_TRADED_PRICE
                                                ? 'last_traded_price'
                                                : 'index_price',
                                        ),
                                        stopLoss: currencyFormatter(
                                            Number(expectedStopLossPrice),
                                            DEPTH[currentSymbol?.minPricePrecision],
                                        ),
                                    })} ${t(
                                        expectedLoss < 0
                                            ? 'expected_profit_message'
                                            : 'expected_loss_message',
                                        {
                                            expectedValue: `${currencyFormatter(
                                                Math.abs(expectedLoss),
                                                DEPTH[currentSymbol?.minPricePrecision],
                                            )} ${currentSymbol.quoteTokenId}`,
                                            ROI:
                                                expectedLossROI === 0
                                                    ? '0.00'
                                                    : currencyFormatter(-1 * expectedLossROI, 2, 2),
                                        },
                                    )}`}
                                </p>
                            </InfoMessage>
                        )
                    )}
                </FlexContainer>
            </ModalContent>
            <ModalActions>
                <Button
                    fullWidth
                    loading={
                        setPositionStopProfitLossLoading || cancelPositionStopProfitLossLoading
                    }
                    onClick={handleConfirm}
                >
                    {t('confirm')}
                </Button>
                <Button fullWidth variant="outlined" onClick={closeHandler}>
                    {t('cancel')}
                </Button>
            </ModalActions>
        </StyledModal>
    );
};

export { PositionTPSLModal };
